import {makeAutoObservable, observable, runInAction} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IUserStore} from "data/stores/user/user.store";
import {RequestState, ModalType} from "data/enums";
import {copyToClipboard, wait, extractErrorMessage, mlbInviteTo} from "data/utils";
import type {IFriend, IFriendsStore} from "data/stores/friends/friends.store";
import React from "react";
import type {IModalsStore} from "data/stores/modals/modals.store";

interface IParams {
	userCode?: string;
}
export interface IFriendsInviteWidgetController extends ViewController<IParams> {
	i18n: ILocalizationStore;

	get friend(): IFriend | null;

	get userCode(): string;

	get copyState(): RequestState;

	get errorMessage(): string | null;

	copyCode: () => void;

	shareInvite: () => void;

	onAddFriend: () => void;

	handleInputChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
}

@injectable()
export class FriendsInviteWidgetController implements IFriendsInviteWidgetController {
	@observable _searchResult: IFriend | null = null;
	@observable _searchError: string | null = null;
	@observable _searchCode: string | null = null;
	@observable _requestCopyCode: RequestState = RequestState.IDLE;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) public readonly _userStore: IUserStore,
		@inject(Bindings.FriendsStore) public readonly _friendsStore: IFriendsStore,
		@inject(Bindings.ModalsStore) public readonly _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	get friend(): IFriend | null {
		return this._searchResult;
	}

	get userCode(): string {
		return this._userStore.user?.code ?? "--";
	}

	get copyState(): RequestState {
		return this._requestCopyCode;
	}

	get errorMessage(): string | null {
		return this._searchError;
	}

	copyCode = () => {
		const code = this._userStore.user?.code;
		if (!code) {
			this._requestCopyCode = RequestState.ERROR;
			return;
		}

		copyToClipboard(code)
			.then(() => {
				runInAction(() => {
					this._requestCopyCode = RequestState.SUCCESS;
				});
			})
			.catch(() => {
				this._requestCopyCode = RequestState.ERROR;
			})
			.finally(() => {
				void wait(10000).then(() => {
					this._requestCopyCode = RequestState.IDLE;
				});
			});
	};

	shareInvite = () => {
		const userCode = this.userCode;
		if (userCode) {
			mlbInviteTo(userCode);
		}
	};

	handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
		const code = event.currentTarget;
		const DEFAULT_CODE_LENGTH = 8;

		this._searchError = null;
		const codeValue = code?.value?.trim();

		if (!codeValue || DEFAULT_CODE_LENGTH !== codeValue.length) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		const isOwnCode = codeValue === this.userCode;
		if (isOwnCode) {
			this._searchError = this.i18n.t(
				"friends.widget.own_code_error",
				"Self invitation is forbidden"
			);
			return;
		}

		this.searchFriend(codeValue);
	};

	searchFriend(code: string) {
		this._friendsStore
			.search(code)
			.then((result) => {
				runInAction(() => {
					this._searchResult = result;
					this._searchCode = code;
				});
			})
			.catch((e) => {
				this._searchError = extractErrorMessage(e);
			});
	}

	onAddFriend = () => {
		const friend = this.friend;
		const searchCode = this._searchCode;

		if (friend && searchCode) {
			void this._friendsStore
				.addFriendByCode(searchCode, friend)
				.then(() => {
					this._searchResult = null;
				})
				.catch((e) => {
					this._modalsStore.showModal(ModalType.ERROR, {
						message: extractErrorMessage(e),
					});
				});
		}
	};

	init(param: IParams) {
		if (param.userCode) {
			this.searchFriend(param.userCode);
		}
	}

	onChange(param: IParams) {
		this.init(param);
	}
}
